import { ApiBaseService, DataServiceAPI, ApiV2Service } from '@/services/api.base.service';
import { SuccessResponse, Widget } from '../types';

export class WidgetService {
  private api: ApiBaseService;
  private dataService: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
    this.dataService = DataServiceAPI;
  }

  public async getWidgets(): Promise<Widget[]> {
    const widgets = await this.api.get<Widget[]>(`dashboard/widgets`);
    if (widgets.length === 0) {
      return this.createDefaultWidgets();
    } else {
      const promises = [];
      for (const widget of widgets) {
        let endpoint = widget.dataEndpoint;
        if (widget.dataEndpoint.charAt(0) === '/') { // Remove when endpoint has beginning / removed.
          endpoint = widget.dataEndpoint.replace(/^\/|\/$/g, '');
        }

        widget.payload = {};

        if (widget.source === 'map') {
          const sitesPromise = this.getSites().then((sites) => {
            widget.payload.sites = [];
            Object.assign(widget.payload.sites, sites);
          });

          const devicesPromise = this.getDevices().then((devices) => {
            widget.payload.devices = [];
            Object.assign(widget.payload.devices, devices);
          });

          const projectsPromise = this.getProjects().then((projects) => {
            widget.payload.projects = [];
            Object.assign(widget.payload.projects, projects);
          });

          promises.push(sitesPromise, devicesPromise, projectsPromise);
        } else if (widget.source === 'table') { // This could be improved.
          if (widget.title === 'projects') {
            const projectsPromise = this.getProjects().then((projects) => {
              widget.payload.headers = [];
              for (const field in projects[0]) {
                if (projects[0].hasOwnProperty(field)) {
                  widget.payload.headers.push({value: field, text: field});
                }
              }
              Object.assign(widget.payload, {values: projects});
            });

            promises.push(projectsPromise);
          }
        } else if (widget.source === 'events') {
          const eventsPromise = this.getEvents(endpoint + '?period=1w&limit=100');
          const gatewayPromise = this.getGatewayEvents('dashboard/gateway/events' + '?period=1w&limit=100');
          const combinedPromise = Promise.all([eventsPromise, gatewayPromise]).then(([dev, gateway]) => {
            const allEvents: any = {};
            allEvents.events = dev.events.concat(gateway.events);
            widget.payload.headers = [];
            if (allEvents.length > 0) {
              for (const field in allEvents[0]) {
                if (allEvents.events[0].hasOwnProperty(field)) {
                  widget.payload.headers.push({ value: field, text: field });
                }
              }
            }
            Object.assign(widget.payload, { values: allEvents });
          });

          promises.push(combinedPromise);
        } else if (widget.source === 'chart') {
          const deviceVersionsPromise = this.getDeviceTypes().then((deviceVersions) => {
            Object.assign(widget.payload, {values: deviceVersions});
          }); // TODO: Get from widget config

          promises.push(deviceVersionsPromise);
        }
      }

      await Promise.all(promises);
      return widgets;
    }
  }

  public async updateWidget(widgetId: string, payload: object): Promise<Widget> {
    return this.api.put(`dashboard/widgets/${widgetId}`, payload);
  }

  public async getMeasurements(organisation: string, device: string, field: string, chartType: string, groupBy: string): Promise<any> {
    let queryString = '?';
    queryString += [ 'line', 'area' ].includes( chartType ) ? `&chartType=series` : '';
    queryString += groupBy ? `&groupBy=${groupBy}` : '';
    return await this.dataService.get(`measurements/${organisation}/${device}/${field}${queryString}`);
  }

  public async removeWidget(widgetId: string): Promise<SuccessResponse> {
    return await this.api.delete(`dashboard/widgets/${widgetId}`);
  }

  public async createDefaultWidgets(): Promise<Widget[]> {
    const defaultWidgets = [{
      source: 'table',
      dataEndpoint: 'dashboard/projects',
      title: 'projects',
      x: 0,
      y: 0,
      width: 3,
      height: 1.5,
      enabledFields: [ 'organisationName', 'projectName', 'groupName', 'status', 'deviceActiveCount', 'deviceFaultCount', 'actions' ],
      config: {},
    },
    {
      source: 'map',
      dataEndpoint: 'dashboard/sites',
      title: 'sites',
      x: 3,
      y: 0,
      width: 3,
      height: 1.5,
      enabledFields: [],
      config: {},
    },
    {
      source: 'events',
      dataEndpoint: 'dashboard/events',
      title: 'events',
      x: 0,
      y: 1.5,
      width: 6,
      height: 3,
      enabledFields: [ 'identifier', 'eventType', 'name', 'projectName', 'eventMessage', 'eventSeverity', 'eventTime', 'actions' ],
      config: {
        organisationFilters: [],
        projectFilters: [],
      },
    },
    /*{
      source: 'table',
      dataEndpoint: 'dashboard/devices',
      title: 'devices',
      x: 2,
      y: 1.4,
      width: 2,
      height: 1.4,
      enabledFields: [ 'deviceId', 'networkId', 'deviceType', 'siteName', 'locationName' ],
      config: {},
    },
    {
      source: 'chart',
      dataEndpoint: 'dashboard/deviceVersions',
      title: 'Measurements',
      x: 4,
      y: 1.4,
      width: 2,
      height: 1.4,
      enabledFields: [],
      config: {},
    }*/];

    await Promise.all(defaultWidgets.map(async (widget: any): Promise<any> => await this.api.post(`dashboard/widgets`, widget)));

    return await this.getWidgets();
  }

  public async getEventsCount(projectName: string): Promise<any> {
    return this.api.get(`dashboard/eventsCount?project=${projectName}`);
  }


  public async getGatewayEventsCount(projectName: string): Promise<any> {
    return this.api.get(`dashboard/gateway/eventsCount?project=${projectName}`);
  }

  public async getShadows(): Promise<any[]> {
    return this.api.get(`dashboard/devices/shadows`);
  }

  public async getDeviceMarkers(params?: { gatewayId?: string, deviceId?: string, search?: string }): Promise<any[]> {
    const queryString = new URLSearchParams(params as any).toString();
    return this.api.get(`dashboard/markers/devices?${queryString}`);
  }

  public async getGatewayMarkers(params: { deviceId?: string, gatewayId?: string, search?: string } = {}): Promise<any[]> {
    const queryString = new URLSearchParams(params as any).toString();
    return this.api.get(`dashboard/markers/gateways?${queryString}`);
  }

  public async getLocations(params: { projectId?: number; search?: string}): Promise<any> {
    const queryString = new URLSearchParams(params as any).toString();
    return this.api.get(`dashboard/locations?${queryString}`);
  }

  public async getBatchDetails(ids: { deviceIds: number[], gatewayIdentifiers: Array<string | null> }): Promise<any[]> {
    return this.api.post(`dashboard/batch`, ids);
  }
  public async getConnections(params: { deviceId?: number, gatewayId?: number, date?: string }): Promise<any> {
    let uri = '';
    if (params.deviceId) {
      uri = `dashboard/devices/${params.deviceId}/radioData`;
    } else if (params.gatewayId) {
      uri = `dashboard/gateways/${params.gatewayId}/radioData`;
    } else {
      return Promise.resolve({ history: [] });
    }

    if (params.date) {
      uri += `?date=${params.date}`;
    }

    return this.api.get(uri);
  }
  public async getHistoryDates(params: { deviceId?: number, gatewayId?: number }): Promise<any> {
    let uri = '';
    if (params.deviceId) {
      uri = `dashboard/devices/${params.deviceId}/radioData/dates`;
    } else if (params.gatewayId) {
      uri = `dashboard/gateways/${params.gatewayId}/radioData/dates`;
    } else {
      return Promise.resolve({ dates: [] });
    }
    return this.api.get(uri);
  }

  public async getConnectionDetails(params: { deviceIds?: number[], gatewayIds?: string[] }): Promise<any> {
    return this.api.post('dashboard/connections/details', params);
  }

  public async getDevicePacketLossForRange(deviceId: number, startDate: string, endDate: string): Promise<any[]> {
    return this.api.get(`dashboard/devices/${deviceId}/radioData/loss?startDate=${startDate}&endDate=${endDate}`);
  }

  public async getDeviceTimelineData(deviceId: number, startDate: string, endDate: string): Promise<any[]> {
    return this.api.get(`dashboard/devices/${deviceId}/radioData/history?startDate=${startDate}&endDate=${endDate}`);
  }

  public async getGateways(): Promise<any[]> {
    return this.api.get(`dashboard/gateways`);
  }

  public async hasGatewaySettings(): Promise<{ result: boolean }> {
    return this.api.get('dashboard/hasGatewaySettings');
  }

  public async getProjects(): Promise<any> {
    return this.api.get('dashboard/projects');
  }

  private async getDevices(): Promise<any> {
    return this.api.get(`dashboard/devices`);
  }

  private async getEvents(endpoint: string): Promise<any> {
    return this.api.get(endpoint);
  }


  private async getGatewayEvents(endpoint: string): Promise<any> {
    return this.api.get(endpoint);
  }

  private async getDeviceTypes(): Promise<any> {
    return this.api.get('dashboard/deviceVersions');
  }

  private async getSites(): Promise<any> {
    return this.api.get('dashboard/sites');
  }
}

export default new WidgetService();
